import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Users, ClipboardList, ArrowRight, Briefcase, FileText } from 'lucide-react';
import { callAdminGetProductOperations } from '../../services/admin';

type ProductId = 'staffaxis' | 'noteaxis';

type Props = {
  productId: ProductId;
};

const productMeta: Record<ProductId, { title: string; subtitle: string; icon: React.ComponentType<any> }> = {
  staffaxis: {
    title: 'StaffAxis Ops',
    subtitle: 'Manage users and audit visibility based on StaffAxis data',
    icon: Briefcase,
  },
  noteaxis: {
    title: 'NoteAxis Ops',
    subtitle: 'Manage users and audit visibility based on NoteAxis data',
    icon: FileText,
  },
};

const AdminProductOps: React.FC<Props> = ({ productId }) => {
  const meta = productMeta[productId];
  const [summary, setSummary] = useState({ totalUsers: 0, activeUsers: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const result = await callAdminGetProductOperations({ productId, limitUsers: 1500 });
        if (!mounted) return;
        setSummary({ totalUsers: result.totalUsers || 0, activeUsers: result.activeUsers || 0 });
      } catch (err: any) {
        if (!mounted) return;
        setError(err?.message || 'Failed to load product summary');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [productId]);

  const cards = useMemo(() => [
    {
      title: 'Users',
      description: `View ${meta.title.replace(' Ops', '')} users and subscription status`,
      icon: Users,
      href: `/admin/${productId}/users`,
      stat: loading ? '...' : String(summary.totalUsers),
      statLabel: 'Total users',
    },
    {
      title: 'Audit Logs',
      description: `Review ${meta.title.replace(' Ops', '')} operational and payment events`,
      icon: ClipboardList,
      href: `/admin/${productId}/audit-logs`,
      stat: loading ? '...' : String(summary.activeUsers),
      statLabel: 'Active users',
    },
  ], [loading, meta.title, productId, summary.activeUsers, summary.totalUsers]);

  const HeaderIcon = meta.icon;

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-10" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
              <HeaderIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">{meta.title}</h1>
              <p className="text-slate-600 dark:text-gray-400">{meta.subtitle}</p>
            </div>
          </div>

          {error && (
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300 text-sm">
              <span>{error}</span>
            </div>
          )}
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <Link key={card.href} to={card.href}>
                <motion.div
                  whileHover={{ y: -4 }}
                  className="glass-effect glass-effect-hover rounded-2xl p-6 shadow-glass h-full flex flex-col cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
                      <Icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{card.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-gray-400 mb-4 flex-1">{card.description}</p>
                  <div className="pt-4 border-t border-slate-200 dark:border-gray-700">
                    <span className="text-2xl font-bold text-slate-900 dark:text-white">{card.stat}</span>
                    <span className="text-sm text-slate-500 dark:text-gray-400 ml-2">{card.statLabel}</span>
                  </div>
                </motion.div>
              </Link>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminProductOps;
